import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { message, Card, ConfigProvider, theme } from 'antd';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';

import { MODULE_IDS } from '../../utils/constants';
import { formatCurrency } from '../../utils/formatters';

import { getAllVendors, createVendor, updateVendor, deleteVendor } from '../../api/vendors.api';

/* ─────────────────────────────────────────────
   Light Theme Override Styles (Scoped to this page)
───────────────────────────────────────────── */
const LightThemeStyles = () => (
  <style>{`
    .light-vendor-page {
      /* Overriding global dark variables for this specific page */
      --bg: #f9fafb;
      --surface: #ffffff;
      --surface-2: #f3f4f6;
      --border: #e5e7eb;
      --border-hi: #d1d5db;
      --text-1: #111827;
      --text-2: #374151;
      --text-3: #6b7280;
      --accent: #f59e0b;
      --accent-dim: rgba(245, 158, 11, 0.15);
      --green: #10b981;
    }

    @keyframes rpFadeUp { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
    @keyframes rpSlideIn { from { transform:translateX(100%) } to { transform:translateX(0) } }
    @keyframes rpPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
    @keyframes rpSpin { to { transform:rotate(360deg) } }

    .rp-live-dot { animation: rpPulse 2s ease-in-out infinite; }
    .rp-drawer-enter { animation: rpSlideIn 0.28s cubic-bezier(0.4,0,0.2,1); }

    .rp-row:hover { background: var(--surface-2) !important; }
    .rp-act-edit:hover  { border-color:#3b82f6 !important; color:#3b82f6 !important; background:rgba(59,130,246,0.1) !important; }
    .rp-act-del:hover   { border-color:#ef4444 !important; color:#ef4444 !important; background:rgba(239,68,68,0.1) !important; }
    .rp-btn-add:hover   { background:#e08d00 !important; }
    .rp-btn-primary:hover:not(:disabled) { background:#e08d00 !important; }
    .rp-btn-ghost:hover { border-color:var(--border-hi) !important; color:var(--text-1) !important; background:var(--surface-2) !important; }
    .rp-form-input:focus  { border-color: var(--accent) !important; background: rgba(245,158,11,0.04) !important; }
    .rp-pg-btn-active { background: var(--accent-dim) !important; border-color: var(--accent) !important; color: var(--accent) !important; }
    .rp-pg-btn:hover:not(:disabled):not(.rp-pg-btn-active) { border-color: var(--border-hi) !important; color: var(--text-1) !important; }
    .rp-pg-btn:disabled { opacity: 0.35; cursor: not-allowed; }
    .rp-spinner { display:inline-block; width:12px; height:12px; border:2px solid rgba(0,0,0,0.25); border-top-color:var(--text-1); border-radius:50%; animation:rpSpin 0.6s linear infinite; }
    
    /* Card Styles Tuning */
    .light-vendor-page .ant-card {
      background: var(--surface);
      border-color: var(--border);
      border-radius: 8px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    }
    .light-vendor-page .ant-card-head {
      border-bottom-color: var(--border);
      padding: 16px 24px;
      min-height: auto;
    }
    .light-vendor-page .ant-card-body {
      padding: 24px;
    }
  `}</style>
);

/* ─────────────────────────────────────────────
   Inline styles
───────────────────────────────────────────── */
const S = {
  page: { fontFamily: "'Barlow', sans-serif", padding: '4px 0', animation: 'rpFadeUp 0.3s ease' },

  pageTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 22, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-1)', display: 'flex', alignItems: 'center', gap: 10, margin: 0 },
  titleDot: { width: 8, height: 8, background: 'var(--accent)', borderRadius: '50%', flexShrink: 0 },
  pageSubtitle: { fontSize: 12, color: 'var(--text-3)', marginTop: 4, letterSpacing: '0.3px', fontWeight: 'normal' },
  
  metaChip: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--text-3)', letterSpacing: '0.5px' },
  liveDot: { width: 5, height: 5, background: 'var(--green)', borderRadius: '50%' },

  btnAdd: { display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'var(--accent)', color: '#0a0c0f', border: 'none', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '0.3px', transition: 'background var(--transition)' },

  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' },
  searchWrap: { position: 'relative', flex: 1, maxWidth: 360 },
  searchIcon: { position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none', display: 'flex' },
  searchInput: { width: '100%', background: 'var(--surface)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: '8px 34px', fontFamily: "'Barlow', sans-serif", fontSize: 13, color: 'var(--text-1)', outline: 'none' },
  searchClear: { position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 },
  resultCount: { fontSize: 12, color: 'var(--text-3)', marginLeft: 'auto' },

  tableWrap: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflowX: 'auto' },
  tableInner: { minWidth: 1000 },
  // 8 Columns: # | Vendor Name | GSTIN | State | Mobile | Email | Balance | Actions
  tableHead: { display: 'grid', gridTemplateColumns: '50px minmax(180px, 1fr) 140px 120px 110px minmax(150px, 1fr) 130px 90px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' },
  th: { padding: '10px 14px', fontSize: 10, fontWeight: 600, letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' },
  tableBody: { maxHeight: 500, overflowY: 'auto' },
  tableRow: { display: 'grid', gridTemplateColumns: '50px minmax(180px, 1fr) 140px 120px 110px minmax(150px, 1fr) 130px 90px', borderBottom: '1px solid var(--border)', transition: 'background var(--transition)', cursor: 'default' },
  td: { padding: '12px 14px', fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', overflow: 'visible' },

  actBtn: { width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-3)', transition: 'all var(--transition)', flexShrink: 0 },
  empty: { padding: '60px 24px', textAlign: 'center' },
  emptyText: { fontSize: 14, color: 'var(--text-3)', marginTop: 8 },

  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--surface-2)' },
  pgInfo: { fontSize: 12, color: 'var(--text-3)' },
  pgBtns: { display: 'flex', gap: 4 },
  pgBtn: { padding: '4px 10px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text-2)', fontSize: 12, cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 400 },
  drawer: { position: 'fixed', right: 0, top: 0, bottom: 0, width: 500, background: 'var(--surface)', borderLeft: '1px solid var(--border)', zIndex: 401, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 15px rgba(0,0,0,0.05)' },
  drawerHeader: { padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0, background: 'var(--surface-2)' },
  drawerTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--text-1)', margin: 0 },
  drawerSubtitle: { fontSize: 11, color: 'var(--text-3)', marginTop: 3 },
  drawerClose: { width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-3)', flexShrink: 0 },
  drawerBody: { flex: 1, overflowY: 'auto', padding: 24 },
  drawerFooter: { padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, flexShrink: 0, background: 'var(--surface-2)' },

  formItem: { marginBottom: 20 },
  formRow: { display: 'flex', gap: 16, marginBottom: 20 },
  formCol: { flex: 1 },
  formLabelRow: { display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 },
  formLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', color: 'var(--text-3)' },
  formRequired: { color: 'var(--accent)', fontSize: 13, lineHeight: 1 },
  formInput: { width: '100%', background: 'var(--bg)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: '9px 12px', fontFamily: "'Barlow', sans-serif", fontSize: 13, color: 'var(--text-1)', outline: 'none', transition: 'border-color var(--transition)' },
  formTextarea: { width: '100%', background: 'var(--bg)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: '9px 12px', fontFamily: "'Barlow', sans-serif", fontSize: 13, color: 'var(--text-1)', outline: 'none', transition: 'border-color var(--transition)', resize: 'vertical', minHeight: 60 },
  formInputError: { borderColor: '#ef4444' },
  formHelp: { fontSize: 11, color: 'var(--text-3)', marginTop: 5, letterSpacing: '0.3px', lineHeight: 1.4 },
  formError: { fontSize: 11, color: '#ef4444', marginTop: 5, display: 'flex', alignItems: 'center', gap: 4 },

  btnPrimary: { flex: 1, padding: '9px 16px', background: 'var(--accent)', color: '#0a0c0f', border: 'none', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, transition: 'background var(--transition)' },
  btnGhost: { padding: '9px 16px', background: 'none', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 500, color: 'var(--text-2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all var(--transition)' },

  delPop: { position: 'absolute', right: 36, top: -20, background: 'var(--surface)', border: '1px solid var(--border-hi)', borderRadius: 'var(--radius)', padding: 12, minWidth: 210, zIndex: 50, boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' },
  delPopTitle: { fontSize: 12, fontWeight: 600, color: 'var(--text-1)', marginBottom: 4 },
  delPopText: { fontSize: 11, color: 'var(--text-3)', marginBottom: 10, lineHeight: 1.5 },
  delPopBtns: { display: 'flex', gap: 6 },
  btnCancelSm: { flex: 1, padding: '5px 8px', background: 'none', border: '1px solid var(--border)', borderRadius: 'var(--radius)', fontSize: 11, color: 'var(--text-2)', cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },
  btnDelSm: { flex: 1, padding: '5px 8px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', fontSize: 11, color: '#ef4444', cursor: 'pointer', fontWeight: 600, fontFamily: "'Barlow', sans-serif" },
};

/* ─────────────────────────────────────────────
   SVG Icons
───────────────────────────────────────────── */
const Icon = {
  Plus: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
  Search: () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
  X: ({ size = 12 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  Edit: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
  Trash: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>,
  Check: () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>,
  Warning: () => <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
  User: () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
};

/* ─────────────────────────────────────────────
   Animated Styles
───────────────────────────────────────────── */
const PageStyles = () => (
  <style>{`
    /* Essential animations, colors are handled by LightThemeStyles */
  `}</style>
);

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */
const DeletePopover = ({ itemName, onConfirm, onCancel }) => (
  <div style={S.delPop} onClick={e => e.stopPropagation()}>
    <div style={S.delPopTitle}>Delete Vendor?</div>
    <div style={S.delPopText}>"{itemName}"<br />This action cannot be undone.</div>
    <div style={S.delPopBtns}>
      <button style={S.btnCancelSm} onClick={onCancel}>Cancel</button>
      <button style={S.btnDelSm} onClick={onConfirm}>Delete</button>
    </div>
  </div>
);

const VendorRow = ({ vendor, index, canEdit, canDelete, onEdit, onDelete, isDeleting }) => {
  const [showDelPop, setShowDelPop] = useState(false);
  const delRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (delRef.current && !delRef.current.contains(e.target)) setShowDelPop(false); };
    if (showDelPop) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDelPop]);

  const isNegative = vendor.openingBalance < 0;

  return (
    <div className="rp-row" style={S.tableRow}>
      <div style={{ ...S.td, color: 'var(--text-3)', fontSize: 12 }}>{index}</div>
      <div style={{ ...S.td, color: 'var(--text-1)', fontWeight: 500 }}>{vendor.vendorName}</div>
      <div style={{ ...S.td, fontSize: 12, letterSpacing: '0.5px' }}>{vendor.gstin || '—'}</div>
      <div style={S.td}>{vendor.stateName || '—'}</div>
      <div style={S.td}>{vendor.mobile || '—'}</div>
      <div style={{ ...S.td, fontSize: 12 }}>{vendor.emailId || '—'}</div>
      <div style={{ ...S.td, fontWeight: isNegative ? 600 : 500, color: isNegative ? '#ef4444' : 'var(--text-1)' }}>
        {formatCurrency(vendor.openingBalance)}
      </div>
      
      <div style={{ ...S.td, gap: 6, position: 'relative' }}>
        {canEdit && (
          <button className="rp-act-edit" style={S.actBtn} onClick={() => onEdit(vendor)} title="Edit">
            <Icon.Edit />
          </button>
        )}
        {canDelete && (
          <div ref={delRef} style={{ position: 'relative' }}>
            <button className="rp-act-del" style={{ ...S.actBtn, ...(isDeleting ? { borderColor: '#ef4444', color: '#ef4444' } : {}) }} onClick={() => setShowDelPop(v => !v)} title="Delete">
              {isDeleting ? <span className="rp-spinner" style={{ borderColor: 'rgba(239,68,68,0.3)', borderTopColor: '#ef4444' }} /> : <Icon.Trash />}
            </button>
            {showDelPop && (
              <DeletePopover itemName={vendor.vendorName} onConfirm={() => { onDelete(vendor.vid); setShowDelPop(false); }} onCancel={() => setShowDelPop(false)} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const VendorDrawer = ({ open, onClose, onSave, editing, loading, control, handleSubmit, errors, reset }) => {
  const drawerRef = useRef(null); // 🔴 NAYA: Drawer ke liye ref

  useEffect(() => {
    if (open) {
      if (editing) {
        reset({
          vendorName: editing.vendorName || '', gstin: editing.gstin || '', address: editing.address || '',
          stateName: editing.stateName || '', stateCode: editing.stateCode || '', mobile: editing.mobile || '',
          emailId: editing.emailId || '', openingBalance: editing.openingBalance || 0
        });
      } else {
        reset({ vendorName: '', gstin: '', address: '', stateName: '', stateCode: '', mobile: '', emailId: '', openingBalance: 0 });
      }

      // 🔴 NAYA: Drawer khulte hi pehle input (Vendor Name) par focus lane ke liye
      setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input[name="vendorName"]');
        if (firstInput) firstInput.focus();
      }, 150);
    }
  }, [open, editing, reset]);

  // 🔴 NAYA: Keyboard Navigation Logic (Enter-to-Next-Field)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Enter') return;
      if (!drawerRef.current || !drawerRef.current.contains(e.target)) return;

      // Agar Textarea (Address) par hain aur Shift+Enter dabaya hai, toh normal new line aane do
      if (e.target.tagName === 'TEXTAREA' && e.shiftKey) {
        return; 
      }

      // Drawer ke andar ke focusable elements dhundho
      const focusable = drawerRef.current.querySelectorAll(
        'input:not([disabled]):not([readonly]), ' +
        'textarea:not([disabled]), ' +
        'button.rp-btn-primary:not([disabled])' // Sirf Save button par focus jayega, Cancel par nahi
      );
      
      const elementsArr = Array.from(focusable);
      const activeIndex = elementsArr.indexOf(document.activeElement);

      if (activeIndex === -1) return;

      // Agar 'Save Vendor' button par Enter dabaya hai, toh form save hone do
      if (document.activeElement.classList.contains('rp-btn-primary')) {
        return; 
      }

      // Aage wale field par jump karo
      e.preventDefault(); 
      const nextIndex = activeIndex + 1;
      if (nextIndex < elementsArr.length) {
        const nextEl = elementsArr[nextIndex];
        nextEl.focus();
        if (nextEl.tagName === 'INPUT' || nextEl.tagName === 'TEXTAREA') {
          nextEl.select(); // Text select kar lo taaki edit karna aasan ho
        }
      }
    };

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div style={S.overlay} onClick={onClose} />
      {/* 🔴 NAYA: Yahan ref={drawerRef} add kiya hai */}
      <div style={S.drawer} className="rp-drawer-enter" ref={drawerRef}>
        <div style={S.drawerHeader}>
          <div>
            <div style={S.drawerTitle}>{editing ? 'Edit Vendor' : 'Add Vendor'}</div>
            <div style={S.drawerSubtitle}>{editing ? 'Update supplier details below' : 'Add a new supplier profile'}</div>
          </div>
          <button style={S.drawerClose} className="rp-act-del" onClick={onClose}><Icon.X /></button>
        </div>

        <div style={S.drawerBody}>
          {/* Vendor Name */}
          <div style={S.formItem}>
            <div style={S.formLabelRow}><span style={S.formLabel}>Vendor Name</span><span style={S.formRequired}>*</span></div>
            <Controller
              name="vendorName" control={control} rules={{ required: 'Vendor Name is required', maxLength: { value: 100, message: 'Max 100 chars' } }}
              render={({ field }) => <input {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.vendorName ? S.formInputError : {}) }} placeholder="Enter full business name" autoComplete="off" />}
            />
            {errors.vendorName && <div style={S.formError}><Icon.Warning />{errors.vendorName.message}</div>}
          </div>

          <div style={S.formRow}>
            {/* GSTIN */}
            <div style={S.formCol}>
              <div style={S.formLabelRow}><span style={S.formLabel}>GSTIN</span></div>
              <Controller
                name="gstin" control={control} 
                rules={{ 
                  maxLength: { value: 15, message: 'Must be exactly 15 chars' },
                  pattern: { value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}$/i, message: 'Invalid GSTIN format' }
                }}
                render={({ field }) => <input {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.gstin ? S.formInputError : {}) }} placeholder="e.g. 27AADCB2230M1Z4" onChange={e => field.onChange(e.target.value.toUpperCase())} autoComplete="off" />}
              />
              {errors.gstin ? <div style={S.formError}><Icon.Warning />{errors.gstin.message}</div> : <div style={S.formHelp}>Format: 22AAAAA0000A1Z5</div>}
            </div>

            {/* Opening Balance */}
            <div style={S.formCol}>
              <div style={S.formLabelRow}><span style={S.formLabel}>Opening Balance (₹)</span></div>
              <Controller
                name="openingBalance" control={control}
                render={({ field }) => <input {...field} type="number" step="any" className="rp-form-input" style={S.formInput} placeholder="0.00" />}
              />
              <div style={S.formHelp}>Positive = they owe you<br/>Negative = you owe them</div>
            </div>
          </div>

          {/* Address */}
          <div style={S.formItem}>
            <div style={S.formLabelRow}><span style={S.formLabel}>Address</span></div>
            <Controller
              name="address" control={control} rules={{ maxLength: { value: 200, message: 'Max 200 chars' } }}
              render={({ field }) => <textarea {...field} rows={2} className="rp-form-input" style={{ ...S.formTextarea, ...(errors.address ? S.formInputError : {}) }} placeholder="Full street address (Shift+Enter for new line)" autoComplete="off" />}
            />
            {errors.address && <div style={S.formError}><Icon.Warning />{errors.address.message}</div>}
          </div>

          <div style={S.formRow}>
            {/* State Name */}
            <div style={S.formCol}>
              <div style={S.formLabelRow}><span style={S.formLabel}>State Name</span></div>
              <Controller
                name="stateName" control={control} rules={{ maxLength: { value: 50, message: 'Max 50 chars' } }}
                render={({ field }) => <input {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.stateName ? S.formInputError : {}) }} placeholder="e.g. Maharashtra" autoComplete="off" />}
              />
              {errors.stateName && <div style={S.formError}><Icon.Warning />{errors.stateName.message}</div>}
            </div>

            {/* State Code */}
            <div style={S.formCol}>
              <div style={S.formLabelRow}><span style={S.formLabel}>State Code</span></div>
              <Controller
                name="stateCode" control={control} rules={{ maxLength: { value: 2, message: 'Max 2 chars' } }}
                render={({ field }) => <input {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.stateCode ? S.formInputError : {}) }} placeholder="e.g. 27" autoComplete="off" />}
              />
              {errors.stateCode && <div style={S.formError}><Icon.Warning />{errors.stateCode.message}</div>}
            </div>
          </div>

          <div style={S.formRow}>
            {/* Mobile */}
            <div style={S.formCol}>
              <div style={S.formLabelRow}><span style={S.formLabel}>Mobile</span></div>
              <Controller
                name="mobile" control={control} rules={{ pattern: { value: /^[0-9]{10}$/, message: 'Must be 10 digits' } }}
                render={({ field }) => <input {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.mobile ? S.formInputError : {}) }} placeholder="10-digit number" autoComplete="off" maxLength={10} />}
              />
              {errors.mobile && <div style={S.formError}><Icon.Warning />{errors.mobile.message}</div>}
            </div>

            {/* Email */}
            <div style={S.formCol}>
              <div style={S.formLabelRow}><span style={S.formLabel}>Email ID</span></div>
              <Controller
                name="emailId" control={control} rules={{ pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' } }}
                render={({ field }) => <input {...field} className="rp-form-input" style={{ ...S.formInput, ...(errors.emailId ? S.formInputError : {}) }} placeholder="email@company.com" autoComplete="off" />}
              />
              {errors.emailId && <div style={S.formError}><Icon.Warning />{errors.emailId.message}</div>}
            </div>
          </div>
        </div>

        <div style={S.drawerFooter}>
          <button style={S.btnGhost} className="rp-btn-ghost" onClick={onClose}>Cancel</button>
          <button style={{ ...S.btnPrimary, opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }} className="rp-btn-primary" onClick={handleSubmit(onSave)} disabled={loading}>
            {loading ? <><span className="rp-spinner" />Saving...</> : <><Icon.Check />Save Vendor</>}
          </button>
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────────────────────────
   Main Page Component
───────────────────────────────────────────── */
const VendorPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [tableParams, setTableParams] = useState({ current: 1, pageSize: 8 });
  const searchRef = useRef(null);

  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();

  const { canAdd, canEdit, canDelete } = usePermissions(MODULE_IDS.VENDOR);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { vendorName: '', gstin: '', address: '', stateName: '', stateCode: '', mobile: '', emailId: '', openingBalance: 0 }
  });

  /* Queries */
  const { data, isLoading: isFetching } = useQuery({
    queryKey: ['vendors', plantId, searchText, tableParams.current, tableParams.pageSize],
    queryFn: () => getAllVendors(plantId, { search: searchText, page: tableParams.current, pageSize: tableParams.pageSize }),
    enabled: !!plantId,
  });

  const vendors = data?.items || [];
  const totalCount = data?.total || 0;

  /* Mutations */
  const createMutation = useMutation({
    mutationFn: (d) => createVendor(plantId, d),
    onSuccess: () => { message.success('Vendor created successfully!'); queryClient.invalidateQueries({ queryKey: ['vendors'] }); setDrawerOpen(false); },
    onError: (err) => message.error(err.message || 'Error creating vendor')
  });

  const updateMutation = useMutation({
    mutationFn: (d) => updateVendor(plantId, editingVendor.vid, d),
    onSuccess: () => { message.success('Vendor updated successfully!'); queryClient.invalidateQueries({ queryKey: ['vendors'] }); setDrawerOpen(false); },
    onError: (err) => message.error(err.message || 'Error updating vendor')
  });

  const deleteMutation = useMutation({
    mutationFn: (vid) => deleteVendor(plantId, vid),
    onSuccess: () => { message.success('Vendor deleted successfully!'); queryClient.invalidateQueries({ queryKey: ['vendors'] }); },
    onError: (err) => message.error(err.message || 'Cannot delete vendor.')
  });

  /* Handlers */
  const openDrawer = (record = null) => { setEditingVendor(record); setDrawerOpen(true); };
  
  const onSave = (formData) => {
    const payload = { ...formData, openingBalance: Number(formData.openingBalance) || 0 };
    if (editingVendor) updateMutation.mutate(payload); else createMutation.mutate(payload);
  };

 

  const totalPages = Math.max(1, Math.ceil(totalCount / tableParams.pageSize));
  const getPageNums = () => {
    const p = tableParams.current, t = totalPages;
    if (t <= 5) return Array.from({ length: t }, (_, i) => i + 1);
    if (p <= 3) return [1, 2, 3, 4, 5];
    if (p >= t - 2) return [t - 4, t - 3, t - 2, t - 1, t];
    return [p - 2, p - 1, p, p + 1, p + 2];
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <ConfigProvider theme={{ algorithm: theme.defaultAlgorithm }}>
      <PageStyles />
      <LightThemeStyles />
      <div className="light-vendor-page" style={S.page}>

        <Card
          title={
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={S.pageTitle}>
                <span style={S.titleDot} />
                Vendor Master
              </div>
              <div style={S.pageSubtitle}>Manage your suppliers, GST details, and opening balances</div>
            </div>
          }
          extra={
            canAdd && (
              <button className="rp-btn-add" style={S.btnAdd} onClick={() => openDrawer(null)}>
                <Icon.Plus /> Add Vendor
              </button>
            )
          }
          styles={{ header: { padding: '20px 24px' } }}
        >

          {/* Toolbar */}
          <div style={S.toolbar}>
            <div style={S.searchWrap}>
              <span style={S.searchIcon}><Icon.Search /></span>
              <input ref={searchRef} className="rp-search-input" style={S.searchInput} placeholder="Search by vendor name, mobile or GSTIN... (Ctrl+F)" value={searchText} onChange={e => { setSearchText(e.target.value); setTableParams(p => ({ ...p, current: 1 })); }} autoComplete="off" />
              {searchText && <button style={S.searchClear} onClick={() => { setSearchText(''); setTableParams(p => ({ ...p, current: 1 })); }}><Icon.X size={11} /></button>}
            </div>
            
            <div style={{ ...S.metaChip, marginLeft: 'auto' }}>
              <span className="rp-live-dot" style={S.liveDot} />
              {totalCount} vendor{totalCount !== 1 ? 's' : ''}
              {searchText && <span style={{ marginLeft: 8 }}> (Results for "{searchText}")</span>}
            </div>
          </div>

          {/* Table */}
          <div style={S.tableWrap}>
            <div style={S.tableInner}>
              <div style={S.tableHead}>
                {['#', 'Vendor Name', 'GSTIN', 'State', 'Mobile', 'Email', 'Balance', 'Actions'].map(h => (
                  <div key={h} style={S.th}>{h}</div>
                ))}
              </div>
              
              <div style={S.tableBody}>
                {isFetching ? (
                  <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                    <span className="rp-spinner" style={{ width: 22, height: 22, borderWidth: 2, borderColor: 'var(--border-hi)', borderTopColor: 'var(--accent)' }} />
                  </div>
                ) : vendors.length === 0 ? (
                  <div style={S.empty}>
                    <div style={{ color: 'var(--text-3)', marginBottom: 8, display: 'flex', justifyContent: 'center' }}><Icon.User /></div>
                    <div style={S.emptyText}>{searchText ? 'No vendors found matching search.' : 'No vendors added yet. Click "Add Vendor" to get started.'}</div>
                  </div>
                ) : (
                  vendors.map((v, i) => (
                    <VendorRow
                      key={v.vid} vendor={v} index={(tableParams.current - 1) * tableParams.pageSize + i + 1}
                      canEdit={canEdit} canDelete={canDelete} onEdit={openDrawer}
                      onDelete={(id) => deleteMutation.mutate(id)}
                      isDeleting={deleteMutation.isPending && deleteMutation.variables === v.vid}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalCount > tableParams.pageSize && (
              <div style={S.pagination}>
                <div style={S.pgInfo}>Showing {(tableParams.current - 1) * tableParams.pageSize + 1}–{Math.min(tableParams.current * tableParams.pageSize, totalCount)} of {totalCount}</div>
                <div style={S.pgBtns}>
                  <button className="rp-pg-btn" style={S.pgBtn} disabled={tableParams.current === 1} onClick={() => setTableParams(p => ({ ...p, current: p.current - 1 }))}>‹ Prev</button>
                  {getPageNums().map(n => (
                    <button key={n} className={`rp-pg-btn${n === tableParams.current ? ' rp-pg-btn-active' : ''}`} style={S.pgBtn} onClick={() => setTableParams(p => ({ ...p, current: n }))}>{n}</button>
                  ))}
                  <button className="rp-pg-btn" style={S.pgBtn} disabled={tableParams.current === totalPages} onClick={() => setTableParams(p => ({ ...p, current: p.current + 1 }))}>Next ›</button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Drawer */}
        <VendorDrawer
          open={drawerOpen} onClose={() => setDrawerOpen(false)} onSave={onSave} editing={editingVendor} loading={isMutating}
          control={control} handleSubmit={handleSubmit} errors={errors} reset={reset}
        />
      </div>
    </ConfigProvider>
  );
};

export default VendorPage;